/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../common/styles/fonts/noto sans/NotoSans-Regular.ttf';
import './App.scss';
import '../../common/styles/typography.scss';
import '../../common/styles/variables.scss';
import '../../common/styles/common.scss';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../common/styles/muiTheme';
import {
  getLanguages,
  translationSelectors,
} from '../../reducers/translationSlice';
import Header from '../Header/Header';
import {
  ADE_ONLINE_UPDATES_FEATURE_FLAG,
  BACKUP_AND_RESTORE,
  BACKUP_VERSION_FF,
  DIAGNOSTIC_PAGE,
  ALARM_CARDS_FF,
  PERSISTENT_SNACKBAR,
  TEMPORARY_SNACKBAR,
} from '../../common/constants';
import AppSidebar from '../../common/components/AppSidebar/AppSidebar';
import TopLevelRouter from './TopLevelRouter';
import { useAppSelector } from '../../reducers/rootReducer';
import useInvalidateAppCache from './hooks/useInvalidateAppCache';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';
import useDisableGlobalPinchZoom from './hooks/useDisableGlobalPinchZoom';
import useRedirectListener from './hooks/useRedirectListener';
import useFeatureFlag from './hooks/useFeatureFlag';
import { selectInterceptorsStatus } from '../../reducers/interceptorsSlice';
import Preloader from '../Preloader/Preloader';
// import useTabVisibility from '../../common/customHooks/useTabVisibility';
import useMopinion from '../../common/customHooks/useMopinion';
import { IS_CURRENT_BROWSER_FIREFOX } from '../Vector/mfrchart/constants';
import useErrorHandling from '../../common/customHooks/useErrorHandling';
import ErrorBoundary from '../../common/components/ErrorBoundary';
import useAuth0WithRedux from '../../auth/useAuth0WithRedux';
import SnackbarsContainer from '../../common/components/Snackbar/SnackbarsContainer';
import useErrorStatus from '../../common/customHooks/useErrorStatus';
import useAlarmIdRepresentation from './hooks/useAlarmIdRepresentation';
import useSnackbarFromFeatureFlag from '../../common/customHooks/useSnackbarFromFeatureFlag';
import useBddManualLogin from './hooks/useBddManualLogin';
import i18n, { updateI18nLanguage } from '../../common/i18n/i18n';
import { getUserDetails } from '../../common/components/UserDetails/actions';

export const featureFlags = [
  ADE_ONLINE_UPDATES_FEATURE_FLAG,
  DIAGNOSTIC_PAGE,
  BACKUP_AND_RESTORE,
  BACKUP_VERSION_FF,
  ALARM_CARDS_FF,
  PERSISTENT_SNACKBAR,
  TEMPORARY_SNACKBAR,
];

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const interceptorsStatus = useAppSelector(selectInterceptorsStatus);
  const translations = useAppSelector(translationSelectors.selectTranslations);
  const preferredLanguage = useAppSelector(
    translationSelectors.selectPreferredLanguages,
  );
  useErrorStatus();
  useAlarmIdRepresentation();
  useSnackbarFromFeatureFlag();
  const {
    login,
    isAuthenticated,
    isLoading: auth0Loading,
  } = useAuth0WithRedux();

  useEffect(() => {
    if (IS_CURRENT_BROWSER_FIREFOX) {
      const bodyElements = document.getElementsByTagName('body')[0];
      bodyElements.classList.add('mozilla-scrollbar');
    }
  }, []);

  // TODO: check it during the cleanup
  // Ensure translations are loaded when user is already authenticated after page refresh
  useEffect(() => {
    if (
      isAuthenticated &&
      !auth0Loading &&
      interceptorsStatus === 'initialized'
    ) {
      // User is already authenticated, ensure translations are loaded
      dispatch(getUserDetails());
      dispatch(getLanguages());
    }
  }, [isAuthenticated, auth0Loading, interceptorsStatus, dispatch]);

  useEffect(() => {
    const isAuthCallback = window.location.pathname === '/auth/callback';
    const isLogoutPage = window.location.pathname === '/logout';
    const urlParams = new URLSearchParams(window.location.search);
    const isBdd = urlParams.get('isBdd') === 'true';

    console.log('🔐 Auth state check:', {
      isAuthenticated,
      auth0Loading,
      isAuthCallback,
      isLogoutPage,
      isBdd,
      pathname: window.location.pathname,
    });

    // Wait for Auth0 to finish loading before making authentication decisions
    if (auth0Loading) {
      console.log('⏳ Auth0 still loading, waiting...');
      return;
    }

    // Don't auto-login if we're on logout page or if logout is in progress
    if (isLogoutPage) {
      console.log('🚫 On logout page, skipping auto-login');
      return;
    }

    if (isBdd && isAuthenticated) {
      console.log('🚀 BDD login detected, navigating to farms...');
      navigate('/farms', { replace: true });
    } else if (!isAuthenticated && !isAuthCallback && !isBdd) {
      console.log('🚀 Initiating login...');
      // const currentPath = window.location.pathname;
      // const searchParams = window.location.search;
      const redirectUrl = localStorage.getItem('redirectUrl');
      localStorage.removeItem('redirectUrl');
      const redirectPath = redirectUrl || '/farms';
      console.log('login with redirect to', redirectPath);
      // localStorage.setItem('loginRedirect', redirectPath);
      login({
        appState: {
          returnTo: redirectPath,
        },
      });
    } else {
      console.log('✅ User already authenticated or special case');
    }
  }, [isAuthenticated, login, auth0Loading, location]);

  useEffect(() => {
    if (!isEmpty(translations)) {
      // Get all namespaces from the response
      const namespaces = Object.keys(translations);
      // Add each namespace bundle
      namespaces.forEach((namespace) => {
        i18n.addResourceBundle(
          preferredLanguage, // language
          namespace, // namespace
          translations[namespace], // data
          true, // deep merge
          true, // overwrite
        );
      });
      updateI18nLanguage(preferredLanguage);
    }
  }, []);

  useInvalidateAppCache();
  useGoogleAnalytics();
  useDisableGlobalPinchZoom();
  useRedirectListener();
  useFeatureFlag(featureFlags, interceptorsStatus);
  // useTabVisibility();
  useMopinion();
  useErrorHandling();
  useBddManualLogin();

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        {interceptorsStatus === 'not-initialized' ? (
          <Preloader activeByDefault />
        ) : (
          <>
            <SnackbarsContainer />
            <Header />
            {isAuthenticated && <AppSidebar />}
            <TopLevelRouter />
          </>
        )}
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
